/**
 * Reaching the data a render was primed with, from either side of the wire.
 *
 * Server: the data the route loader fetched, handed down through context so two renders in
 * flight at once cannot see each other's data.
 * Client: `window.__GOZIG_SSR__`, read once at module load and handed out at most once per key
 * per mount — so the first render matches the markup React is hydrating onto, while a later
 * navigation back to the same route fetches fresh content instead of replaying page load.
 */

import { createContext, useContext, useState } from 'react';
import type { SsrPayload } from './ssrTypes';

const payload: SsrPayload | null
    = typeof window === 'undefined' ? null : window.__GOZIG_SSR__ ?? null;

/** True when this document arrived with server-rendered markup, i.e. hydrate rather than mount. */
export const wasServerRendered = (): boolean => payload != null;

export const ssrFlags = (): Record<string, boolean> | undefined => payload?.flags;

/** Set only during a server render; null in the browser. */
export const SsrDataContext = createContext<Record<string, unknown> | null>(null);

const taken = new Set<string>();

function takeFromDocument<T>(key: string): T | undefined {
    if (!payload || taken.has(key)) return undefined;
    taken.add(key);
    return payload.data[key] as T | undefined;
}

/**
 * The primed value for `key`, or undefined when nothing was primed. `undefined` means "fetch as
 * usual"; a loader that looked and found nothing stores `null`, which is an answer.
 *
 *     const primed = useSsrPrimed<Article>(ssrDataKeys.article(slug));
 *     const [item, setItem] = useState(primed ?? null);
 *     const [loading, setLoading] = useState(primed === undefined);
 */
export function useSsrPrimed<T>(key: string | null): T | undefined {
    const serverData = useContext(SsrDataContext);
    const [value] = useState<T | undefined>(() => {
        if (key == null) return undefined;
        if (serverData) return serverData[key] as T | undefined;
        return takeFromDocument<T>(key);
    });
    return value;
}
